<template>
  <section ref="root" class="ach-history">
    <div class="ach-header">
      <div class="header-left">
        <h2>{{ $t('achievementHistory.title') }}</h2>
        <div v-if="!isLocked && loaded && !error" class="ach-info">
          {{ $tc('achievementHistory.rangeCount', total, { count: total }) }}
        </div>
      </div>
      <div v-if="!isLocked && total > 0" class="header-controls">
        <div class="ach-control">
          <label for="ach-order">{{ $t('games.sortBy') }}</label>
          <select id="ach-order" :value="order" @change="setOrder($event.target.value)">
            <option value="desc">{{ $t('achievementHistory.newest') }}</option>
            <option value="asc">{{ $t('achievementHistory.oldest') }}</option>
          </select>
        </div>
        <div class="ach-control">
          <label for="ach-per-page">{{ $t('games.show') }}</label>
          <select id="ach-per-page" :value="perPage" @change="setPerPage($event.target.value)">
            <option v-for="n in perPageOptions" :key="n" :value="n">{{ n }}</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Unclaimed: the history is what claiming buys. The rows behind the blur
         are placeholders, not this profile's data — the api sends none. -->
    <div v-if="isLocked" class="ach-locked">
      <div class="ach-locked-preview" aria-hidden="true">
        <div v-for="n in 3" :key="n" class="ach-row ach-row--ghost">
          <span class="ach-icon ach-icon--ghost"></span>
          <span class="ach-ghost-lines">
            <span class="ach-ghost-line"></span>
            <span class="ach-ghost-line ach-ghost-line--short"></span>
          </span>
        </div>
      </div>
      <div class="ach-locked-body">
        <strong class="ach-locked-title">{{ $t('achievementHistory.lockedTitle') }}</strong>
        <p class="ach-locked-text">{{ $t('achievementHistory.lockedBody') }}</p>
        <ClaimCard :profile-steam-id="steamId" variant="achievements" />
      </div>
    </div>

    <p v-else-if="error" class="ach-status">{{ $t('achievementHistory.error') }}</p>
    <p v-else-if="!loaded" class="ach-status">{{ $t('achievementHistory.loading') }}</p>
    <p v-else-if="!items.length" class="ach-status">{{ $t('achievementHistory.empty') }}</p>

    <template v-else>
      <div v-for="day in days" :key="day.date" class="ach-day">
        <div class="ach-day-header">
          <span class="ach-day-date">{{ formatDate(day.date, { includeTime: false }) }}</span>
          <span class="ach-day-count">{{ $t('games.unlockedCount', { count: day.items.length }) }}</span>
        </div>
        <div
          v-for="item in day.items"
          :key="`${item.appId}:${item.unlocktime}:${item.name}`"
          class="ach-row"
        >
          <img
            v-if="item.icon"
            class="ach-icon"
            :src="iconUrl(item.icon)"
            :alt="item.name"
            loading="lazy"
            @error="$event.target.style.visibility = 'hidden'"
          >
          <span v-else class="ach-icon ach-icon--ghost"></span>
          <div class="ach-text">
            <strong class="ach-name">{{ item.name }}</strong>
            <span v-if="item.description" class="ach-desc">{{ item.description }}</span>
            <span class="ach-game">{{ item.gameName }}</span>
          </div>
          <span class="ach-time">{{ formatTime(item.unlocktime) }}</span>
        </div>
      </div>

      <div v-if="totalPages > 1" class="pagination-container">
        <div class="pagination">
          <button type="button" class="page-btn" :title="$t('games.firstPage')" :disabled="page === 1 || loading" @click="goToPage(1)">⟪</button>
          <button type="button" class="page-btn" :title="$t('games.prevPage')" :disabled="page === 1 || loading" @click="goToPage(page - 1)">‹</button>
          <div class="page-numbers">
            <button
              v-for="n in visiblePages"
              :key="n"
              type="button"
              class="page-btn"
              :class="{ active: n === page }"
              :disabled="loading"
              @click="goToPage(n)"
            >
              {{ n }}
            </button>
          </div>
          <button type="button" class="page-btn" :title="$t('games.nextPage')" :disabled="page === totalPages || loading" @click="goToPage(page + 1)">›</button>
          <button type="button" class="page-btn" :title="$t('games.lastPage')" :disabled="page === totalPages || loading" @click="goToPage(totalPages)">⟫</button>
        </div>
        <div class="pagination-info">
          {{ $t('games.pageOf', { page, total: totalPages }) }}
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { playtimeAPI } from '../../services/api';
import { achievementIconUrl } from '../../utils/achievements';
import ClaimCard from './ClaimCard.vue';

export default {
    name: 'AchievementHistorySection',
    components: { ClaimCard },
    props: {
        steamId: { type: String, default: '' },
        // The profile payload's `claimed`. Strictly `false` locks the section;
        // anything else asks the api, which holds the real gate.
        claimed: { type: Boolean, default: null },
        startDate: { type: String, default: '' },
        endDate: { type: String, default: '' },
        formatDate: { type: Function, required: true },
    },
    data () {
        return {
            // Nothing is fetched until the section scrolls near the viewport:
            // most visitors never reach the bottom of the dashboard, and they
            // should cost MySQL nothing.
            inView: false,
            items: [],
            total: 0,
            page: 1,
            totalPages: 1,
            perPage: 20,
            perPageOptions: [20, 50, 100],
            order: 'desc',
            loaded: false,
            loading: false,
            error: false,
            // The api refused (a profile payload cached before a claim was
            // undone, say). Same view as `claimed === false`.
            refused: false,
            requestSeq: 0,
        };
    },
    computed: {
        isLocked () {
            return this.claimed === false || this.refused;
        },
        queryKey () {
            return `${this.steamId}|${this.startDate}|${this.endDate}|${this.claimed}`;
        },
        // Up to five page buttons centred on the current page, shifted at the
        // ends so there are always five while five exist.
        visiblePages () {
            const span = Math.min(5, this.totalPages);
            const start = Math.min(Math.max(1, this.page - 2), this.totalPages - span + 1);

            return Array.from({ length: span }, (_, i) => start + i);
        },
        // Grouped by `date`, the UTC day the whole dashboard is keyed on, so a
        // day here is the same day as on the chart above it.
        days () {
            const groups = [];

            for (const item of this.items) {
                const last = groups[groups.length - 1];

                if (last && last.date === item.date) {
                    last.items.push(item);
                } else {
                    groups.push({ date: item.date, items: [item] });
                }
            }

            return groups;
        },
    },
    watch: {
        queryKey () {
            this.reset();
            this.loadPage(1);
        },
    },
    mounted () {
        if (typeof window === 'undefined' || typeof window.IntersectionObserver !== 'function') {
            this.inView = true;
            this.loadPage(1);

            return;
        }

        this.observer = new window.IntersectionObserver((entries) => {
            if (entries.some(e => e.isIntersecting)) {
                this.inView = true;
                this.observer.disconnect();
                this.observer = null;
                this.loadPage(1);
            }
        }, { rootMargin: '300px 0px' });
        this.observer.observe(this.$refs.root);
    },
    beforeUnmount () {
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        reset () {
            this.requestSeq += 1;
            this.items = [];
            this.total = 0;
            this.page = 1;
            this.totalPages = 1;
            this.loaded = false;
            this.loading = false;
            this.error = false;
            this.refused = false;
        },
        goToPage (n) {
            if (n >= 1 && n <= this.totalPages && n !== this.page) {
                this.loadPage(n);
            }
        },
        setOrder (value) {
            this.order = value === 'asc' ? 'asc' : 'desc';
            this.loadPage(1);
        },
        setPerPage (value) {
            this.perPage = Number(value) || 20;
            this.loadPage(1);
        },
        async loadPage (page) {
            if (!this.inView || !this.steamId || this.claimed === false) {
                return;
            }

            // A newer request supersedes an older one; its answer is dropped
            // below rather than the click being ignored.
            const seq = ++this.requestSeq;

            this.loading = true;

            try {
                const params = { page, limit: this.perPage, order: this.order };

                if (this.startDate) params.startDate = this.startDate;
                if (this.endDate) params.endDate = this.endDate;

                const { data } = await playtimeAPI.getAchievementHistory(this.steamId, params);

                // Superseded while in flight: its answer is for a page or a
                // window no longer on screen.
                if (seq !== this.requestSeq) {
                    return;
                }

                this.items = data.items || [];
                this.total = Number(data.total) || 0;
                this.page = Number(data.page) || 1;
                this.totalPages = Number(data.totalPages) || 1;
                this.loaded = true;
                this.error = false;
            } catch (err) {
                if (seq !== this.requestSeq) {
                    return;
                }

                if (err?.response?.status === 403) {
                    this.refused = true;
                } else {
                    this.error = true;
                }
            } finally {
                if (seq === this.requestSeq) {
                    this.loading = false;
                }
            }
        },
        iconUrl (icon) {
            return achievementIconUrl(icon);
        },
        // The visitor's local clock time. The day header is the UTC day, so for
        // a visitor far from UTC an unlock just after local midnight sits under
        // the previous day — the same convention as every other date here.
        formatTime (unlocktime) {
            if (!unlocktime) {
                return '';
            }

            const locale = this.$i18n.locale === 'tr' ? 'tr-TR' : 'en-US';

            return new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(new Date(unlocktime * 1000));
        },
    },
};
</script>

<style scoped>
.ach-history {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
}

.ach-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 16px;
}

.header-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.ach-control {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ach-control label {
  color: var(--muted);
  font-size: 0.9rem;
}

.ach-control select {
  padding: 6px 10px;
  border: 1px solid var(--panel-border);
  border-radius: 6px;
  background: #0f141a;
  color: var(--text);
  font-size: 0.9rem;
}

.ach-header h2 {
  margin: 0 0 5px 0;
  color: var(--text);
}

.ach-info,
.ach-status {
  color: var(--muted);
  font-size: 0.9rem;
}

.ach-status {
  margin: 0;
}

.ach-day + .ach-day {
  margin-top: 18px;
}

.ach-day-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding-bottom: 8px;
  border-bottom: 1px solid var(--panel-border);
  margin-bottom: 4px;
}

.ach-day-date {
  color: var(--text);
  font-weight: 600;
}

.ach-day-count {
  color: var(--muted);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  font-weight: 600;
}

.ach-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
}

.ach-row + .ach-row {
  border-top: 1px solid rgba(255, 255, 255, 0.04);
}

.ach-icon {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  flex-shrink: 0;
  object-fit: cover;
}

.ach-icon--ghost {
  display: inline-block;
  background: #0f141a;
}

.ach-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;
}

.ach-name {
  color: var(--text);
  overflow-wrap: anywhere;
}

.ach-desc {
  color: var(--muted);
  font-size: 0.85rem;
  overflow-wrap: anywhere;
}

.ach-game {
  color: var(--primary);
  font-size: 0.8rem;
  overflow-wrap: anywhere;
}

.ach-time {
  color: var(--muted);
  font-size: 0.85rem;
  flex-shrink: 0;
}

/* Same chrome as the games table's pagination. */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid var(--panel-border);
  flex-wrap: wrap;
  gap: 15px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 5px;
  flex-wrap: wrap;
}

.page-numbers {
  display: flex;
  gap: 2px;
}

.page-btn {
  background: var(--panel);
  border: 1px solid var(--panel-border);
  color: var(--muted);
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.9rem;
  min-width: 40px;
  text-align: center;
}

.page-btn:hover:not(:disabled) {
  background: #0f141a;
  border-color: var(--primary);
  color: var(--primary);
}

.page-btn:disabled {
  background: #0f141a;
  color: #555;
  cursor: not-allowed;
  border-color: #222;
}

.page-btn.active {
  background: var(--primary);
  color: #0b0e12;
  border-color: var(--primary);
}

.pagination-info {
  color: var(--muted);
  font-size: 0.9rem;
}

.ach-locked {
  position: relative;
}

.ach-locked-preview {
  position: absolute;
  inset: 0;
  overflow: hidden;
  filter: blur(3px);
  opacity: 0.35;
  pointer-events: none;
}

.ach-ghost-lines {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.ach-ghost-line {
  height: 10px;
  width: 60%;
  border-radius: 4px;
  background: #2a3f52;
}

.ach-ghost-line--short {
  width: 35%;
}

.ach-locked-body {
  position: relative;
  min-height: 170px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 16px;
}

.ach-locked-title {
  color: var(--text);
}

.ach-locked-text {
  color: var(--muted);
  font-size: 0.9rem;
  margin: 6px 0 8px;
  max-width: 460px;
}
</style>
